import { FC, RefObject, useRef, useState } from 'react';
import { Button, Grid, useMediaQuery, useTheme } from '@mui/material';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import strings from '../../../common/Translation/Translate';
import PreviewContract from '../NewBooking/PreviewContract';
import ShareIcon from '@mui/icons-material/Share';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DrawerMenu from '../../../components/DrawerMenu';
import {
  downloadExportFile,
  getDrawerWidth,
  removeErrorFieldsFromValues,
} from '../../../utils/validators/HelperFunctions';
import { FormComponent } from '../../../utils/validators/HelperComponents';
import Form from '../../../components/Form';
import { SendBeoEmailForm } from './Forms/SendBeoEmailForm';
import { useSelector } from 'react-redux';
import { Privilege } from '../../../utils/redux/reducer/authentication-slice';
import { privileges } from '../../../utils/constants/privileges';
import axiosInstance from '../../../utils/axios';
import { URLS } from '../../../utils/constants/urls';
import axios from 'axios';
import Config from '../../../utils/Config';
import { fetchFromStorage } from '../../../utils/storage';
import { identifiers } from '../../../utils/constants/identifiers';

interface BeoActionButtonsProps {
  id: number;
  beoId: string;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  setMessage: (msg: any) => void;
  handleCatch: (err: any) => void;
  handleSave: (e: any, preview: boolean) => Promise<boolean>;
  fetchInitialData: () => void;
  pageResponse: any;
  runWithSaveIfDirty: (action: () => void | Promise<void>) => void;
}

type refType = RefObject<Form | null | undefined>;

const BeoActionButtons: FC<BeoActionButtonsProps> = ({
  id,
  isLoading,
  setIsLoading,
  setMessage,
  handleCatch,
  handleSave,
  fetchInitialData,
  pageResponse,
  runWithSaveIfDirty,
}) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const userPrivileges = useSelector(Privilege);

  let drawerFormRef: refType = useRef();
  let drawerFormRefAdmin: refType = useRef();
  const accessToken = fetchFromStorage(identifiers.access_token) as any;

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openDrawerAdmin, setOpenDrawerAdmin] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [closeModal, setCloseModal] = useState(false);
  const [previewBEO, setPreviewBEO] = useState('');
  const [fileName, setFileName] = useState<any>('BookingEventOrder');
  const [sendEmailResponse, setSendEmailResponse] = useState<any>({});
  const [sendEmailAdminResponse, setSendEmailAdminResponse] = useState<any>({});
  /** Sync guard so a second Send click in the same tick cannot start another POST. */
  const savingRef = useRef(false);
  const [isSending, setIsSending] = useState(false);

  const handleCustomerEmailOpen = () => {
    setSendEmailResponse({ to: pageResponse.customerEmail });
    setOpenDrawer(true);
  };

  const handleAdminEmailOpen = () => {
    setSendEmailAdminResponse({
      to: pageResponse.distributionList,
      subject: (pageResponse?.beoName || '') + ' - ' + pageResponse.beoReferenceNo,
    });
    setOpenDrawerAdmin(true);
  };

  const handleCustomerEmailClose = () => {
    setOpenDrawer(false);
    drawerFormRef.current?.resetForm();
    setHasError(false);
  };

  const handleAdminEmailClose = () => {
    setOpenDrawerAdmin(false);
    drawerFormRefAdmin.current?.resetForm();
    setHasError(false);
  };

  const sendEmail = async (e: any, sendToAdmin: boolean) => {
    e.preventDefault();
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    setIsSending(true);
    try {
      const ref = sendToAdmin ? drawerFormRefAdmin : drawerFormRef;
      const { getFormData } = ref.current!;
      const { formData, isFormValid } = getFormData();

      if (!isFormValid) return setHasError(true);

      const cleanedData = removeErrorFieldsFromValues(formData);
      const requestBody = {
        ...cleanedData,
        to: cleanedData.to.split(',').map((item: any) => item.trim()),
        cc:
          cleanedData.cc
            ?.split(',')
            .map((x: string) => x.trim())
            .filter(Boolean) || [],
        bcc:
          cleanedData.bcc
            ?.split(',')
            .map((x: string) => x.trim())
            .filter(Boolean) || [],
      };
      setIsLoading(true);
      await axiosInstance.post(sendToAdmin ? URLS.sendBEOEmailToAdmin(id) : URLS.sendBEOEmail(id), requestBody);

      setMessage({ display: true, severity: 'success', message: strings.beo_email_successMsg });
      setOpenDrawer(false);
      setOpenDrawerAdmin(false);
      fetchInitialData();
    } catch (error: any) {
      handleCatch(error);
    } finally {
      savingRef.current = false;
      setIsSending(false);
      setIsLoading(false);
    }
  };

  const handlePreviewBEO = async () => {
    try {
      setIsLoading(true);
      if (screenSizeDownSm) {
        await downloadBEO();
        return;
      }
      const { data } = await axiosInstance.get(URLS.previewBeo(id));
      setFileName(data.fileName);
      setPreviewBEO(data.html);
      setCloseModal(true);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };
  const downloadBEO = async () => {
    try {
      setIsLoading(true);
      const response = await axios({
        url: `${Config.BaseURL}${URLS.downloadBEOPdf(id)}`,
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
        responseType: 'blob',
      });
      downloadExportFile(response, 'pdf', fileName);
    } catch (err) {
      handleCatch(err);
    } finally {
      setIsLoading(false);
    }
  };

  const actionButtonSx = {
    minHeight: 32,
    py: 0.4,
    px: 1.5,
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'none',
    my: 0,
    '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
  };
  const actionIconSx = { fontSize: 16 };

  return (
    <Grid container spacing={1} sx={{ mb: 0 }} alignItems="center">
      {/* Save BEO */}
      {userPrivileges?.includes(privileges.add_beo) && !pageResponse.isPastBooking && !pageResponse.sentToStaff && (
        <Grid item xs={12} sm={4} md={2} lg={1}>
          <PrimaryButton
            fullWidth
            onClick={(e: any) => handleSave(e, false)}
            variant="contained"
            disabled={isLoading || isSending}
            sx={actionButtonSx}>
            {strings.common_button_save}
          </PrimaryButton>
        </Grid>
      )}

      {/* Preview BEO */}
      {userPrivileges?.includes(privileges.preview_beo) &&
        pageResponse.id &&
        (!pageResponse.isPastBooking ? (
          <Grid item xs={screenSizeDownSm && 12}>
            <Button
              fullWidth
              size="small"
              onClick={() => runWithSaveIfDirty(handlePreviewBEO)}
              variant="outlined"
              startIcon={<VisibilityIcon sx={actionIconSx} />}
              sx={actionButtonSx}>
              {screenSizeDownSm ? strings.downloadPreviewBeo : strings.bookings_button_preview_beo}
            </Button>
          </Grid>
        ) : (
          <Grid item xs={screenSizeDownSm && 12}>
            <Button
              fullWidth
              size="small"
              onClick={handlePreviewBEO}
              variant="outlined"
              startIcon={<VisibilityIcon sx={actionIconSx} />}
              sx={actionButtonSx}>
              {screenSizeDownSm ? strings.downloadPreviewBeo : strings.bookings_button_preview_beo}
            </Button>
          </Grid>
        ))}

      {closeModal && (
        <Grid item xs={screenSizeDownSm && 12}>
          <PreviewContract
            headerText={strings.bookings_button_preview_beo}
            downloadButtonText={strings.bookings_button_download_beo}
            downloadContract={downloadBEO}
            pdfUrl={previewBEO}
            handleClose={() => {
              setPreviewBEO('');
              setCloseModal(false);
            }}
            openPdfDrawer={closeModal}
            loading={isLoading}
          />
        </Grid>
      )}

      {/* Send BEO to Customer */}
      {userPrivileges?.includes(privileges.send_beo) && pageResponse.id && (
        <Grid item xs={screenSizeDownSm && 12}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={() => runWithSaveIfDirty(handleCustomerEmailOpen)}
            startIcon={<ShareIcon sx={actionIconSx} />}
            sx={actionButtonSx}>
            {strings.bookings_button_send_beo}
          </Button>
          <DrawerMenu
            width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd)}
            open={openDrawer}
            handleClose={handleCustomerEmailClose}
            closeButton={true}
            title={strings.bookings_button_send_beo}>
            <FormComponent
              hasError={hasError}
              formRef={drawerFormRef as RefObject<Form>}
              pageResponse={sendEmailResponse}
              handleSave={(e: any) => sendEmail(e, false)}
              formModel={SendBeoEmailForm(strings, false)}
              buttonText={strings.bookings_button_send}
              disabled={isLoading || isSending}
            />
          </DrawerMenu>
        </Grid>
      )}

      {/* Send BEO to Staff */}
      {userPrivileges?.includes(privileges.send_beo) && pageResponse.id && (
        <Grid item xs={screenSizeDownSm && 12}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={() => runWithSaveIfDirty(handleAdminEmailOpen)}
            startIcon={<SupervisorAccountIcon sx={actionIconSx} />}
            sx={actionButtonSx}>
            {pageResponse.sentToStaff
              ? strings.bookings_button_send_revision_to_staff
              : strings.bookings_button_send_beo_to_admin}
          </Button>
          <DrawerMenu
            width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd)}
            open={openDrawerAdmin}
            handleClose={handleAdminEmailClose}
            closeButton={true}
            title={
              pageResponse.sentToStaff
                ? strings.bookings_button_send_revision_to_staff
                : strings.bookings_button_send_beo_to_admin
            }>
            <FormComponent
              hasError={hasError}
              formRef={drawerFormRefAdmin as RefObject<Form>}
              pageResponse={sendEmailAdminResponse}
              handleSave={(e: any) => sendEmail(e, true)}
              formModel={SendBeoEmailForm(strings, true)}
              buttonText={strings.bookings_button_send}
              disabled={isLoading || isSending}
            />
          </DrawerMenu>
        </Grid>
      )}
    </Grid>
  );
};

export default BeoActionButtons;
